/**
 * Onboarding context and provider for managing tour state
 */

import {
  createContext,
  useState,
  useCallback,
  useEffect,
  type ReactNode,
} from "react";
import {
  getOnboardingCompleted,
  setOnboardingCompleted,
  getOnboardingType,
  setOnboardingType,
  type OnboardingType,
} from "./onboarding-storage";
import { DESKTOP_TOUR_STEPS, MOBILE_TOUR_STEPS } from "./tour-steps";
import { useMediaQuery } from "@/hooks/use-media-query";

export type OnboardingContextType = {
  // State
  showWelcomeModal: boolean;
  isTouring: boolean;
  currentStep: number;
  totalSteps: number;
  onboardingType: OnboardingType;
  isMobile: boolean;

  // Actions
  selectNewTrader: () => void;
  selectProfessional: () => void;
  nextStep: () => void;
  prevStep: () => void;
  skipTour: () => void;
  restartTour: () => void;
};

export const OnboardingContext = createContext<OnboardingContextType | null>(null);

type OnboardingProviderProps = {
  children: ReactNode;
};

export const OnboardingProvider = ({
  children,
}: OnboardingProviderProps) => {
  const isMobile = useMediaQuery("(max-width: 1000px)");
  const totalSteps = isMobile ? MOBILE_TOUR_STEPS.length : DESKTOP_TOUR_STEPS.length;

  // Check if we should show onboarding on mount
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const [isTouring, setIsTouring] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [onboardingType, setOnboardingTypeState] = useState<OnboardingType>(null);

  // Initialize from localStorage on mount
  useEffect(() => {
    const completed = getOnboardingCompleted();
    const type = getOnboardingType();
    setOnboardingTypeState(type);

    if (!completed) {
      // Small delay to let the app render first
      const timer = setTimeout(() => {
        setShowWelcomeModal(true);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, []);

  const selectNewTrader = useCallback(() => {
    setOnboardingType("new");
    setOnboardingTypeState("new");
    setShowWelcomeModal(false);
    setCurrentStep(0);
    setIsTouring(true);
  }, []);

  const selectProfessional = useCallback(() => {
    setOnboardingType("professional");
    setOnboardingTypeState("professional");
    setOnboardingCompleted(true);
    setShowWelcomeModal(false);
    setIsTouring(false);
  }, []);

  const nextStep = useCallback(() => {
    if (currentStep < totalSteps - 1) {
      setCurrentStep((s) => s + 1);
    } else {
      // Completed the tour
      setOnboardingCompleted(true);
      setIsTouring(false);
    }
  }, [currentStep, totalSteps]);

  const prevStep = useCallback(() => {
    if (currentStep > 0) {
      setCurrentStep((s) => s - 1);
    }
  }, [currentStep]);

  const skipTour = useCallback(() => {
    setOnboardingCompleted(true);
    setIsTouring(false);
    setShowWelcomeModal(false);
  }, []);

  const restartTour = useCallback(() => {
    // Don't reset localStorage completely, just restart the tour
    setCurrentStep(0);
    setIsTouring(true);
    setShowWelcomeModal(false);
  }, []);

  return (
    <OnboardingContext.Provider
      value={{
        showWelcomeModal,
        isTouring,
        currentStep,
        totalSteps,
        onboardingType,
        isMobile,
        selectNewTrader,
        selectProfessional,
        nextStep,
        prevStep,
        skipTour,
        restartTour,
      }}
    >
      {children}
    </OnboardingContext.Provider>
  );
};

